import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'selected' | 'outline';
  size?: 'sm' | 'md';
  removable?: boolean;
  onRemove?: () => void;
  leftIcon?: ReactNode;
}

export const Chip = forwardRef<HTMLButtonElement, ChipProps>(
  (
    {
      variant = 'default',
      size = 'md',
      removable = false,
      onRemove,
      leftIcon,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const handleRemove = (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove?.();
    };

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full font-medium transition-all',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          {
            'px-2.5 py-1 text-xs': size === 'sm',
            'px-3 py-1.5 text-sm': size === 'md',
          },
          {
            'bg-gray-100 text-gray-700 hover:bg-gray-200': variant === 'default',
            'bg-primary text-white hover:bg-primary-600': variant === 'selected',
            'border border-border bg-white text-gray-700 hover:bg-gray-50':
              variant === 'outline',
          },
          className
        )}
        {...props}
      >
        {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
        <span>{children}</span>
        {removable && (
          <span
            role="button"
            tabIndex={-1}
            onClick={handleRemove}
            className={cn(
              'flex-shrink-0 rounded-full p-0.5 transition-colors',
              {
                'hover:bg-gray-300': variant === 'default',
                'hover:bg-primary-700': variant === 'selected',
                'hover:bg-gray-200': variant === 'outline',
              }
            )}
          >
            <X className={cn({ 'h-3 w-3': size === 'sm', 'h-3.5 w-3.5': size === 'md' })} />
          </span>
        )}
      </button>
    );
  }
);

Chip.displayName = 'Chip';
